// Builds display graphs (metrics, colors, sizes, key connectors) from API
// responses. Positions are NOT set here — the layout runs in the worker
// (see layoutPipeline.ts / layoutWorker.ts).
import Graph from 'graphology';
import { SocialGraphAnalyzeResponse, CaseGraphResponse } from '../../utils/socialGraphApi';
import { normalizeAffiliationType } from '../../utils/affiliationTypes';
import { MENTION_TYPE_COLORS } from './timelineUtils';
import {
  COMMUNITY_PALETTE,
  computeEdgeColor,
  computeNodeColor,
  DEFAULT_COLOR_MODE,
  DEFAULT_INTER_CLUSTER_MODE,
  platformColorForRank,
  zscoreNodeBucket,
} from './graphColors';
import {
  ActorConnectionStats,
  audienceBucket,
  classicEdgeSize,
  EdgeAttributes,
  FAR_ZOOM_EDGE_LIMIT,
  GraphStats,
  MemberConnections,
  NodeAttributes,
  NodeMetricValues,
  NodeSizeMetric,
  ScopeFlow,
  TOP_VOLUME_EDGE_COUNT,
  TOP_VOLUME_MIN_EDGES,
} from './graphTypes';

// Flags the TOP_VOLUME_EDGE_COUNT heaviest actor PAIRS by summed interactions.
// Ranking by pair (not directed edge) keeps both directions of a reciprocal
// relation in step — lighting A→B but not B→A would read as a bug. Static per
// graph: the rank ignores filters so the picture doesn't reshuffle as the user
// narrows the canvas.
function markTopVolumeEdges(graph: Graph<NodeAttributes, EdgeAttributes>): void {
  if (graph.size < TOP_VOLUME_MIN_EDGES) return;
  const pairTotals = new Map<string, number>();
  graph.forEachEdge((_edge, attrs, source, target) => {
    const key = source < target ? `${source}\u0000${target}` : `${target}\u0000${source}`;
    pairTotals.set(key, (pairTotals.get(key) ?? 0) + attrs.totalCount);
  });
  const topPairs = new Set(
    [...pairTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_VOLUME_EDGE_COUNT)
      .map(([key]) => key),
  );
  graph.forEachEdge((edge, _attrs, source, target) => {
    const key = source < target ? `${source}\u0000${target}` : `${target}\u0000${source}`;
    if (topPairs.has(key)) graph.setEdgeAttribute(edge, 'topVolume', true);
  });
}

// Flags every edge ranked below FAR_ZOOM_EDGE_LIMIT by interactions so the
// canvas can skip them while zoomed out (hover reducer). Static per graph, like
// the top-volume pairs; graphs within the limit get no flags at all.
function markThinnedEdges(graph: Graph<NodeAttributes, EdgeAttributes>): void {
  if (graph.size <= FAR_ZOOM_EDGE_LIMIT) return;
  const ranked = graph.mapEdges((edge, attrs) => ({ edge, totalCount: attrs.totalCount }));
  ranked.sort((a, b) => b.totalCount - a.totalCount);
  for (let i = FAR_ZOOM_EDGE_LIMIT; i < ranked.length; i++) {
    graph.setEdgeAttribute(ranked[i].edge, 'thinned', true);
  }
}

// Calmer arcs inside communities; strong bend on long-range edges so they arc around
// cluster interiors instead of plowing through them
export const INTRA_COMMUNITY_CURVATURE = 0.15;
export const INTER_COMMUNITY_CURVATURE = 0.45;
// Nodes with the highest metric value always show their label (constellation names)
export const TOP_LABEL_COUNT = 8;

// "Critical bridges" are the KEY CONNECTORS of the graph: the actors that tie the
// most communities together. Ranked by the number of distinct communities an
// actor is linked to (its own excluded), ties broken by betweenness, then degree;
// the top KEY_CONNECTOR_COUNT actors that reach at least MIN_CONNECTOR_COMMUNITIES
// other communities are flagged. This replaced Tarjan connector points
// (team decision 2026-09-17): dense mention graphs have no single point of
// failure — measured on 30 real graphs, plain connector points flagged only
// hubs holding one or two dangling duplicate profiles (110 on graph 277, none
// cutting off more than 5 actors), and a size-floored variant fired on 1 graph
// in 30. A ranking always answers "who links the clusters".
export const KEY_CONNECTOR_COUNT = 10;
export const MIN_CONNECTOR_COMMUNITIES = 2;

export function findKeyConnectors(graph: Graph<NodeAttributes, EdgeAttributes>): Set<string> {
  const candidates: { id: string; spanned: number; betweenness: number; degree: number }[] = [];
  graph.forEachNode((id, attrs) => {
    const otherCommunities = new Set<number>();
    graph.forEachNeighbor(id, (_neighbor, neighborAttrs) => {
      if (neighborAttrs.community !== attrs.community) otherCommunities.add(neighborAttrs.community);
    });
    if (otherCommunities.size >= MIN_CONNECTOR_COMMUNITIES) {
      candidates.push({ id, spanned: otherCommunities.size, betweenness: attrs.betweenness ?? 0, degree: graph.degree(id) });
    }
  });
  candidates.sort(
    (a, b) => b.spanned - a.spanned || b.betweenness - a.betweenness || b.degree - a.degree || a.id.localeCompare(b.id),
  );
  return new Set(candidates.slice(0, KEY_CONNECTOR_COUNT).map((c) => c.id));
}

// "High-Interaction Actors" are the upper outliers of the graph's interaction
// volume (an actor's volume = the summed totalCount of every edge touching it,
// both directions). Interaction counts are heavy-tailed — most actors sit at a
// handful, a few hubs at thousands — so the classic Tukey rule is applied on
// the log scale: an actor qualifies when log(1 + volume) exceeds
// Q3 + HIGH_INTERACTION_IQR_FACTOR × IQR of all actors' log volumes, i.e. it is
// multiplicatively well above the typical actor. The factor is deliberately
// looser than Tukey's 1.5 (PM asked for a wider category, 2026-10-16): on
// simulated power-law graphs 1.5 flagged 3–7 % of actors, 0.75 flags 6–13 %,
// while the raw-scale rule would hover at 10–15 % regardless. Two
// guards keep the category meaningful: graphs with fewer than
// MIN_HIGH_INTERACTION_NODES actors have no distribution to be an outlier of,
// and when the distribution is degenerate (three quarters of the actors share
// one count, so the fence collapses onto Q3) the category is capped to the
// top HIGH_INTERACTION_MAX_SHARE of actors by volume. Like key connectors the
// flags are static per graph — computed on the full built graph, not the
// filtered canvas — and ties are broken by id so a rebuild never reshuffles.
export const HIGH_INTERACTION_IQR_FACTOR = 0.75;
export const MIN_HIGH_INTERACTION_NODES = 5;
export const HIGH_INTERACTION_MAX_SHARE = 0.2;

const quantile = (sortedAsc: number[], p: number): number => {
  const pos = (sortedAsc.length - 1) * p;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (pos - lo);
};

// summed totalCount of every edge touching the actor (inbound + outbound)
export function interactionVolume(graph: Graph<NodeAttributes, EdgeAttributes>, id: string): number {
  let volume = 0;
  graph.forEachEdge(id, (_edge, attrs) => {
    volume += attrs.totalCount;
  });
  return volume;
}

// An actor's connections split by direction the way the Connections tab
// classifies them (a neighbour is inbound-only, outbound-only or mutual) with
// the interactions on those connections — the readout under member names in
// the group panel. Reads the full graph, so filters don't change the numbers.
export function memberConnections(graph: Graph<NodeAttributes, EdgeAttributes>, id: string): MemberConnections {
  const result: MemberConnections = {
    inbound: { connections: 0, interactions: 0 },
    outbound: { connections: 0, interactions: 0 },
    mutual: { connections: 0, interactions: 0 },
  };
  graph.forEachNeighbor(id, (neighbor) => {
    const outEdge = graph.edge(id, neighbor);
    const inEdge = graph.edge(neighbor, id);
    const outCount = outEdge ? graph.getEdgeAttribute(outEdge, 'totalCount') : 0;
    const inCount = inEdge ? graph.getEdgeAttribute(inEdge, 'totalCount') : 0;
    const stat = outEdge && inEdge ? result.mutual : inEdge ? result.inbound : result.outbound;
    stat.connections += 1;
    stat.interactions += inCount + outCount;
  });
  return result;
}

// The Actor panel's Connections card. Unlike memberConnections the in/out
// numbers INCLUDE mutual pairs (a mutual neighbour did reach the actor and was
// reached), so each count equals the length of the Connections-tab list its
// drill opens; interactions are the volume on those edges, mutual = both
// directions of the reciprocal pairs.
export function actorConnectionStats(graph: Graph<NodeAttributes, EdgeAttributes>, id: string): ActorConnectionStats {
  const result: ActorConnectionStats = {
    inbound: { connections: 0, interactions: 0 },
    outbound: { connections: 0, interactions: 0 },
    mutual: { connections: 0, interactions: 0 },
  };
  graph.forEachNeighbor(id, (neighbor) => {
    const outEdge = graph.edge(id, neighbor);
    const inEdge = graph.edge(neighbor, id);
    const outCount = outEdge ? graph.getEdgeAttribute(outEdge, 'totalCount') : 0;
    const inCount = inEdge ? graph.getEdgeAttribute(inEdge, 'totalCount') : 0;
    if (inEdge) {
      result.inbound.connections += 1;
      result.inbound.interactions += inCount;
    }
    if (outEdge) {
      result.outbound.connections += 1;
      result.outbound.interactions += outCount;
    }
    if (inEdge && outEdge) {
      result.mutual.connections += 1;
      result.mutual.interactions += inCount + outCount;
    }
  });
  return result;
}

export function findHighInteractionActors(graph: Graph<NodeAttributes, EdgeAttributes>): Set<string> {
  if (graph.order < MIN_HIGH_INTERACTION_NODES) return new Set();
  const volumes: { id: string; volume: number }[] = [];
  graph.forEachNode((id) => volumes.push({ id, volume: interactionVolume(graph, id) }));
  const logs = volumes.map(({ volume }) => Math.log1p(volume)).sort((a, b) => a - b);
  const q1 = quantile(logs, 0.25);
  const q3 = quantile(logs, 0.75);
  const fence = q3 + HIGH_INTERACTION_IQR_FACTOR * (q3 - q1);
  const outliers = volumes
    .filter(({ volume }) => volume > 0 && Math.log1p(volume) > fence)
    .sort((a, b) => b.volume - a.volume || a.id.localeCompare(b.id));
  const cap = Math.max(1, Math.ceil(graph.order * HIGH_INTERACTION_MAX_SHARE));
  return new Set(outliers.slice(0, cap).map(({ id }) => id));
}

// Platform scheme hues for one graph: platforms ranked by actor count (most
// first, ties by name so a rebuild never swaps hues) and mapped onto
// PLATFORM_PALETTE, wrapping past its length. Computed on the full built graph
// once per build — never on the filtered canvas (see PLATFORM_PALETTE).
export function rankPlatformColors(graph: Graph<NodeAttributes, EdgeAttributes>): Map<string, string> {
  const counts = new Map<string, number>();
  graph.forEachNode((_id, attrs) => counts.set(attrs.platform, (counts.get(attrs.platform) ?? 0) + 1));
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return new Map(ranked.map(([platform], rank) => [platform, platformColorForRank(rank)]));
}

export function getDominantMentionType(mentionTypes: Record<string, number>): string {
  let dominant = 'MENTION';
  let maxCount = 0;
  for (const [type, count] of Object.entries(mentionTypes)) {
    if (count > maxCount) {
      maxCount = count;
      dominant = type;
    }
  }
  return dominant;
}

export function getNodeMetricValue(node: NodeMetricValues, metric: NodeSizeMetric): number {
  switch (metric) {
    case 'pagerank': return node.pagerank;
    case 'betweenness': return node.betweenness;
    case 'hub_score': return node.hubScore;
    case 'authority_score': return node.authorityScore;
    case 'degree': return node.degree;
    case 'anomaly': return node.anomaly;
  }
}

// the metric values a built node carries (same numbers the builders read off the DTO)
function nodeMetricsOf(attrs: NodeAttributes): NodeMetricValues {
  return {
    pagerank: attrs.pagerank,
    betweenness: attrs.betweenness,
    hubScore: attrs.hubScore,
    authorityScore: attrs.authorityScore,
    degree: attrs.degree,
    anomaly: attrs.weightedAnomalyScore ?? 0,
  };
}

export const MIN_NODE_SIZE = 2.5;
export const NODE_SIZE_RANGE = 10;

// the case (repost) engine computes only betweenness and degree
export const caseSizeMetric = (sizeMetric: NodeSizeMetric): NodeSizeMetric =>
  sizeMetric === 'betweenness' || sizeMetric === 'degree' ? sizeMetric : 'degree';

// Node size + Cosmograph-style persistent labels (the TOP_LABEL_COUNT nodes by the
// active metric keep their label visible so the main structures are identifiable
// without hovering), derived from the metrics stored on the nodes. Runs at build
// time and again when the user switches the size metric: sizes update in place
// and nothing moves (scaling plan 1.6) — one batched attribute event, no rebuild.
export function applySizeMetric(graph: Graph<NodeAttributes, EdgeAttributes>, sizeMetric: NodeSizeMetric): void {
  let maxMetric = 0;
  const ranked: { id: string; value: number }[] = [];
  graph.forEachNode((id, attrs) => {
    const value = getNodeMetricValue(nodeMetricsOf(attrs), sizeMetric);
    maxMetric = Math.max(maxMetric, value);
    ranked.push({ id, value });
  });
  const topIds = new Set(
    ranked
      .sort((a, b) => b.value - a.value)
      .slice(0, TOP_LABEL_COUNT)
      .map((entry) => entry.id),
  );
  // no attribute hints on purpose: sigma rebuilds its label grid and forced-label
  // set only on a full re-index, which hints would let it skip
  graph.updateEachNodeAttributes((id, attrs) => {
    const value = getNodeMetricValue(nodeMetricsOf(attrs), sizeMetric);
    const size = MIN_NODE_SIZE + (maxMetric > 0 ? (value / maxMetric) * NODE_SIZE_RANGE : 0);
    const forceLabel = topIds.has(id);
    if (attrs.size === size && (attrs.forceLabel ?? false) === forceLabel) return attrs;
    return { ...attrs, size, forceLabel };
  });
}

// Builds the graph with metrics, colors, and sizes but NO positions — the layout
// runs asynchronously in the worker (see the rebuild effect)
export function buildGraphFromResponse(
  response: SocialGraphAnalyzeResponse,
  sizeMetric: NodeSizeMetric,
): { graph: Graph<NodeAttributes, EdgeAttributes>; stats: GraphStats } {
  const graph = new Graph<NodeAttributes, EdgeAttributes>({ type: 'directed', multi: false });

  for (const n of response.nodes) {
    const id = String(n.id);

    // fields from backend PR #823 — absent on graphs generated before it deployed
    const affiliations = (n.affiliations ?? []).map((a) => ({ entityType: normalizeAffiliationType(a.entity_type) ?? a.entity_type, country: a.country }));
    const affiliationTypes = Array.from(new Set(affiliations.map((a) => a.entityType)));
    const affiliationCountries = Array.from(
      new Set(affiliations.map((a) => a.country).filter((c): c is string => c !== null)),
    );

    graph.addNode(id, {
      label: n.name,
      x: 0,
      y: 0,
      size: 3,
      color: '#999999',
      inEvent: n.in_scope,
      community: n.community,
      betweenness: n.betweenness,
      pagerank: n.pagerank,
      hubScore: n.hub_score,
      authorityScore: n.authority_score,
      actorRole: n.actor_role,
      platform: n.platform,
      platformBase: '',
      sourceUrl: n.url,
      isKeyConnector: false,
      isHighRisk: n.is_high_risk,
      // any affiliation implies state affiliation (same semantics as case graphs)
      isStateAffiliated: affiliationTypes.length > 0,
      interactions: 0,
      isHighInteraction: false,
      discreditations: n.discreditations ?? [],
      affiliations,
      affiliationTypes,
      affiliationCountries,
      inauthenticTypes: n.inauthentic_types ?? [],
      tags: (n.tags ?? []).map((tag) => ({ tag: tag.tag, nameEn: tag.name_en, nameUk: tag.name_uk })),
      degree: n.degree,
      // profile fields the mention DTO doesn't carry
      isVerified: null,
      originCountry: null,
      audience: null,
      audienceBucket: null,
      isDust: false,
      reciprocity: n.reciprocity,
      clusteringCoeff: n.clustering_coeff,
      platformDiversity: n.platform_diversity,
      bridgeScore: n.bridge_score,
      weightGini: n.weight_gini,
      avgZScore: n.avg_z_score,
      weightedAnomalyScore: n.weighted_anomaly_score,
      zscoreBucket: n.avg_z_score != null ? zscoreNodeBucket(n.avg_z_score) : null,
    });
  }

  // platform hues by rank, stamped on nodes now and on edges below
  const platformColors = rankPlatformColors(graph);
  graph.forEachNode((id, attrs) =>
    graph.setNodeAttribute(id, 'platformBase', platformColors.get(attrs.platform) ?? platformColorForRank(0)),
  );

  for (const e of response.edges) {
    const sourceId = String(e.source);
    const targetId = String(e.target);

    if (!graph.hasNode(sourceId) || !graph.hasNode(targetId)) continue;
    if (graph.hasEdge(sourceId, targetId)) continue;

    const dominant = getDominantMentionType(e.mention_types ?? {});
    const sourceCommunity = graph.getNodeAttribute(sourceId, 'community');
    const interCommunity = sourceCommunity !== graph.getNodeAttribute(targetId, 'community');
    const sourceInScope = graph.getNodeAttribute(sourceId, 'inEvent');
    const targetInScope = graph.getNodeAttribute(targetId, 'inEvent');
    const scopeFlow: ScopeFlow =
      sourceInScope && targetInScope ? 'internal' : sourceInScope ? 'outbound' : targetInScope ? 'inbound' : 'external';
    // initial color matches the default UI state (community scheme, cross-cluster
    // dimmed); the sync effect recomputes it when the user changes either control
    const colorBases = {
      mentionBase: MENTION_TYPE_COLORS[dominant] ?? '#7C8597',
      communityBase: COMMUNITY_PALETTE[sourceCommunity % COMMUNITY_PALETTE.length],
      platformBase: graph.getNodeAttribute(sourceId, 'platformBase'),
      interCommunity,
      reciprocal: false,
      zScore: e.z_score,
      scopeFlow,
    };
    graph.addEdge(sourceId, targetId, {
      dominantMentionType: dominant,
      mentionTypes: e.mention_types ?? {},
      weight: e.weight,
      totalCount: e.total_count,
      color: computeEdgeColor(colorBases, DEFAULT_COLOR_MODE, DEFAULT_INTER_CLUSTER_MODE),
      ...colorBases,
      // classic sizing here regardless of the "Thickness by interactions"
      // switch — the sync effect in SocialGraphPOC re-applies the current
      // switch state right after every build (same pattern as arrows/colors)
      size: classicEdgeSize(e.weight, false),
      type: 'curved',
      curvature: interCommunity ? INTER_COMMUNITY_CURVATURE : INTRA_COMMUNITY_CURVATURE,
    });
  }

  if (graph.order === 0) {
    return {
      graph,
      stats: {
        nodeCount: 0, edgeCount: 0, communityCount: 0, externalCount: 0,
        keyConnectorCount: 0, totalActorsInScope: response.global_metrics.total_actors_in_scope,
        actorsWithoutInteractions: response.global_metrics.actors_without_interactions ?? 0,
        actorsOverLimit: response.global_metrics.actors_over_limit ?? 0,
        density: response.global_metrics.density, reciprocity: response.global_metrics.reciprocity,
      },
    };
  }

  graph.forEachEdge((edge, attrs, source, target) => {
    if (graph.hasEdge(target, source)) {
      graph.mergeEdgeAttributes(edge, {
        reciprocal: true,
        color: computeEdgeColor({ ...attrs, reciprocal: true }, DEFAULT_COLOR_MODE, DEFAULT_INTER_CLUSTER_MODE),
        size: classicEdgeSize(attrs.weight, true),
      });
    }
  });

  const keyConnectors = findKeyConnectors(graph);
  const highInteraction = findHighInteractionActors(graph);

  const communities = new Set<number>();
  let externalCount = 0;
  graph.forEachNode((id, attrs) => {
    communities.add(attrs.community);
    if (!attrs.inEvent) externalCount += 1;
    graph.mergeNodeAttributes(id, {
      // initial color matches the default UI state; the sync effect recomputes on change
      color: computeNodeColor(attrs, DEFAULT_COLOR_MODE),
      isKeyConnector: keyConnectors.has(id),
      interactions: interactionVolume(graph, id),
      isHighInteraction: highInteraction.has(id),
    });
  });

  applySizeMetric(graph, sizeMetric);
  markTopVolumeEdges(graph);
  markThinnedEdges(graph);

  return {
    graph,
    stats: {
      nodeCount: graph.order,
      edgeCount: graph.size,
      communityCount: communities.size,
      externalCount,
      keyConnectorCount: keyConnectors.size,
      totalActorsInScope: response.global_metrics.total_actors_in_scope,
      actorsWithoutInteractions: response.global_metrics.actors_without_interactions ?? 0,
      actorsOverLimit: response.global_metrics.actors_over_limit ?? 0,
      density: response.global_metrics.density,
      reciprocity: response.global_metrics.reciprocity,
    },
  };
}

export function buildCaseGraphFromResponse(
  response: CaseGraphResponse,
  sizeMetric: NodeSizeMetric,
): { graph: Graph<NodeAttributes, EdgeAttributes>; stats: GraphStats } {
  const graph = new Graph<NodeAttributes, EdgeAttributes>({ type: 'directed', multi: false });


  for (const n of response.nodes) {
    const id = String(n.id);

    const affiliations = (n.affiliations ?? []).map((a) => ({ entityType: normalizeAffiliationType(a.entity_type) ?? a.entity_type, country: a.country }));

    graph.addNode(id, {
      label: n.name,
      x: 0,
      y: 0,
      size: 3,
      color: '#999999',
      inEvent: true,
      community: n.community,
      betweenness: n.betweenness,
      pagerank: 0,
      hubScore: 0,
      authorityScore: 0,
      actorRole: n.role,
      platform: n.platform,
      platformBase: '',
      sourceUrl: n.url,
      isKeyConnector: false,
      isHighRisk: n.is_high_risk,
      isStateAffiliated: n.is_state_affiliated,
      interactions: 0,
      isHighInteraction: false,
      discreditations: n.discreditations ?? [],
      affiliations,
      affiliationTypes: Array.from(new Set(affiliations.map((a) => a.entityType))),
      affiliationCountries: Array.from(
        new Set(affiliations.map((a) => a.country).filter((c): c is string => c !== null)),
      ),
      // the case (repost) engine doesn't compute inauthenticity buckets or tags
      inauthenticTypes: [],
      tags: [],
      degree: n.degree,
      isVerified: n.is_verified,
      originCountry: n.origin_country,
      audience: n.audience,
      audienceBucket: audienceBucket(n.audience),
      isDust: false,
      reciprocity: null,
      clusteringCoeff: null,
      platformDiversity: null,
      bridgeScore: null,
      weightGini: null,
      avgZScore: null,
      weightedAnomalyScore: null,
      zscoreBucket: null,
    });
  }

  // platform hues by rank, stamped on nodes now and on edges below
  const platformColors = rankPlatformColors(graph);
  graph.forEachNode((id, attrs) =>
    graph.setNodeAttribute(id, 'platformBase', platformColors.get(attrs.platform) ?? platformColorForRank(0)),
  );

  for (const e of response.edges) {
    // The backend orients case edges producer → amplifier (original author →
    // reposter, "content flow"). Every direction cue in this UI — arrows,
    // Inbound/Outbound, the in/out hover colours, the legend — reads the source
    // as the actor performing the action (the way actor graphs are oriented:
    // reposter → reposted), so the edge is flipped here: source = amplifier
    // (who reposted), target = producer (who got reposted). A Producer then
    // shows only inbound connections, matching its definition. Keep the
    // timeline edge keys in timelineUtils in the same orientation.
    const sourceId = String(e.amplifier);
    const targetId = String(e.producer);
    if (!graph.hasNode(sourceId) || !graph.hasNode(targetId)) continue;
    if (graph.hasEdge(sourceId, targetId)) continue;

    const sourceCommunity = graph.getNodeAttribute(sourceId, 'community');
    const interCommunity = sourceCommunity !== graph.getNodeAttribute(targetId, 'community');
    const colorBases = {
      mentionBase: MENTION_TYPE_COLORS.REPOST,
      communityBase: COMMUNITY_PALETTE[sourceCommunity % COMMUNITY_PALETTE.length],
      platformBase: graph.getNodeAttribute(sourceId, 'platformBase'),
      interCommunity,
      reciprocal: false,
      zScore: 0,
      // case graphs have no external actors — every edge is inside the scope
      scopeFlow: 'internal' as ScopeFlow,
    };
    graph.addEdge(sourceId, targetId, {
      dominantMentionType: 'REPOST',
      mentionTypes: {},
      weight: e.weight,
      totalCount: e.weight,
      color: computeEdgeColor(colorBases, DEFAULT_COLOR_MODE, DEFAULT_INTER_CLUSTER_MODE),
      ...colorBases,
      size: classicEdgeSize(e.weight, false),
      type: 'curved',
      curvature: interCommunity ? INTER_COMMUNITY_CURVATURE : INTRA_COMMUNITY_CURVATURE,
    });
  }

  if (graph.order === 0) {
    return {
      graph,
      stats: {
        nodeCount: 0, edgeCount: 0, communityCount: 0, externalCount: 0,
        keyConnectorCount: 0, totalActorsInScope: 0, density: 0, reciprocity: 0,
        actorsWithoutInteractions: 0, actorsOverLimit: 0,
        producersCount: response.global_metrics.producers_count,
        amplifiersCount: response.global_metrics.amplifiers_count,
      },
    };
  }

  graph.forEachEdge((edge, attrs, source, target) => {
    if (graph.hasEdge(target, source)) {
      graph.mergeEdgeAttributes(edge, {
        reciprocal: true,
        color: computeEdgeColor({ ...attrs, reciprocal: true }, DEFAULT_COLOR_MODE, DEFAULT_INTER_CLUSTER_MODE),
        size: classicEdgeSize(attrs.weight, true),
      });
    }
  });

  const keyConnectors = findKeyConnectors(graph);
  const highInteraction = findHighInteractionActors(graph);

  const communities = new Set<number>();
  graph.forEachNode((id, attrs) => {
    communities.add(attrs.community);
    graph.mergeNodeAttributes(id, {
      color: computeNodeColor(attrs, DEFAULT_COLOR_MODE),
      isKeyConnector: keyConnectors.has(id),
      interactions: interactionVolume(graph, id),
      isHighInteraction: highInteraction.has(id),
    });
  });

  // case graph only has betweenness + degree; fall back for unsupported metrics
  applySizeMetric(graph, caseSizeMetric(sizeMetric));
  markTopVolumeEdges(graph);
  markThinnedEdges(graph);

  return {
    graph,
    stats: {
      nodeCount: graph.order,
      edgeCount: graph.size,
      communityCount: communities.size,
      externalCount: 0,
      keyConnectorCount: keyConnectors.size,
      totalActorsInScope: response.global_metrics.nodes_count,
      actorsWithoutInteractions: 0,
      actorsOverLimit: 0,
      density: 0,
      reciprocity: 0,
      producersCount: response.global_metrics.producers_count,
      amplifiersCount: response.global_metrics.amplifiers_count,
    },
  };
}

